import mime from 'mime'
import { useEffect, useRef, useState } from 'react'
import { ProgressBar } from 'react-bootstrap'
import { FileKind, type FileInfo } from '../../models/File'
import { useAppDispatch } from '../../store/hooks'
import { useLazyDownloadGlobalFileRequestQuery, useUploadFileMutation, useUploadFileRequestMutation } from '../../store/media/file'
import { useDeleteFileMutation } from '../../store/media/media'
import { useGetUserByIdQuery, useSaveUserByIdMutation, userApi } from '../../store/user/user'
import { getInitials } from '../../views/utils'
import { Button } from '../Button/Button'
import { UserAvatar } from '../UserAvatar/UserAvatar'
import { useUserAvatarProps } from '../UserAvatar/useUserAvatarProps'
import { AvatarCropModal } from './AvatarCropModal'
import { renameFileToMimeType } from './utils'

const MB = 1024 * 1024
const MAX_SIZE = 5 * MB
const ACCEPTED_TYPES = ['image/gif', 'image/jpeg', 'image/png', 'image/webp']

type UserAvatarUploadProps = {
  accountId: string
  userId: string
}

type AvatarError = { title: string; description: string }

export const UserAvatarUpload: React.FC<UserAvatarUploadProps> = ({ accountId, userId }) => {
  const userQuery = useGetUserByIdQuery({ accountId, userId })
  const userData = userQuery.data?.data
  const settings = userData?.settings ?? {}
  const initials = getInitials(userData?.name)

  const avatar = settings.assets?.avatar?.avatarFile ?? null
  const fileInputRef = useRef<HTMLInputElement>(null)
  const previewUrlRef = useRef<string | null>(null)
  const cropOwnedRef = useRef(false)

  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null)
  const [error, setError] = useState<AvatarError | null>(null)
  const [cropSrc, setCropSrc] = useState<string | null>(null)
  const [cropFileName, setCropFileName] = useState('')
  const [progress, setProgress] = useState(0)

  const [downloadReq, { isLoading: isLazyDownload }] = useLazyDownloadGlobalFileRequestQuery()
  const [uploadFileRequest, { isLoading: isRequestingUpload }] = useUploadFileRequestMutation()
  const [uploadFile, { isLoading: isUploadingFile }] = useUploadFileMutation()
  const [deleteFile] = useDeleteFileMutation()
  const [saveUser, { isLoading: isSavingAvatar }] = useSaveUserByIdMutation()
  const [deleteAvatar, { isLoading: isDeleting }] = useSaveUserByIdMutation()
  const dispatch = useAppDispatch()

  const uploading = isRequestingUpload || isUploadingFile || isSavingAvatar
  const imageUrl = avatar?.fileId ? resolvedUrl : null
  const userAvatarProps = useUserAvatarProps({ width: '80px', height: '80px' })

  const patchAvatar = (file: FileInfo | null) => {
    dispatch(
      userApi.util.updateQueryData('getUserById', { accountId, userId }, (draft) => {
        draft.data.settings = { ...draft.data.settings, assets: { avatar: file ? { avatarFile: file } : null } }
      }),
    )
  }

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!avatar?.fileId) {
      return
    }
    let active = true
    downloadReq({ accountId, fileId: avatar.fileId })
      .unwrap()
      .then(({ data }) => {
        if (active) {
          setResolvedUrl(data.location)
          if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current)
            previewUrlRef.current = null
          }
        }
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [accountId, avatar?.fileId, downloadReq])

  const resetInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const triggerBrowse = () => {
    setError(null)
    resetInput()
    fileInputRef.current?.click()
  }

  const processFile = (file?: File) => {
    if (!file) {
      return
    }
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError({ title: 'Wrong format', description: 'Only JPG, PNG, GIF, WEBP files are accepted.' })
      resetInput()
      return
    }
    if (file.size > MAX_SIZE) {
      setError({ title: 'File too large', description: 'Please upload an image under 5 MB.' })
      resetInput()
      return
    }
    setError(null)
    setCropFileName(file.name)
    cropOwnedRef.current = true
    setCropSrc(URL.createObjectURL(file))
  }

  const openCurrentInCrop = () => {
    if (!imageUrl) {
      return
    }
    setError(null)
    cropOwnedRef.current = false
    setCropFileName(avatar?.fileName ?? '')
    setCropSrc(imageUrl)
  }

  const handleFilePicked = (evt: React.ChangeEvent<HTMLInputElement>) => {
    processFile(evt.target.files?.[0])
  }

  const handleDrop = (evt: React.DragEvent) => {
    evt.preventDefault()
    processFile(evt.dataTransfer.files?.[0])
  }

  const closeCropModal = () => {
    if (cropOwnedRef.current && cropSrc) {
      URL.revokeObjectURL(cropSrc)
    }
    cropOwnedRef.current = false
    setCropSrc(null)
    resetInput()
  }

  const reconcileFileName = (blob: Blob, fileName: string) => {
    return blob.type !== mime.getType(fileName) ? renameFileToMimeType(fileName, blob.type) : fileName
  }

  const handleCropConfirm = async (blob: Blob) => {
    const previousFileId = avatar?.fileId ?? null
    const fileName = reconcileFileName(blob, cropFileName)
    closeCropModal()
    setProgress(0)
    try {
      const file = new File([blob], fileName, { type: blob.type })
      const { data } = await uploadFileRequest({
        accountId,
        body: { fileName, fileSize: file.size, contentType: file.type, kind: FileKind.USER_AVATAR_ASSET },
      }).unwrap()
      setProgress(50)
      await uploadFile({ uploadUrl: data.fileUploadUrl, uploadParameters: data.fileUploadParameters, file }).unwrap()
      setProgress(90)
      await saveUser({
        accountId,
        userId,
        userData: {
          settings: { ...settings, assets: { avatar: { avatarFile: { fileId: data.fileInfo.fileId, fileName, fileSize: file.size } } } },
        },
      }).unwrap()
      setProgress(100)
      const previewUrl = URL.createObjectURL(blob)
      previewUrlRef.current = previewUrl
      setResolvedUrl(previewUrl)
      patchAvatar({ fileId: data.fileInfo.fileId, fileName, fileSize: file.size })
      if (previousFileId && previousFileId !== data.fileInfo.fileId) {
        deleteFile({ accountId, fileId: previousFileId })
          .unwrap()
          .catch(() => undefined)
      }
    } catch {
      setError({ title: 'Upload failed', description: 'Please try again.' })
    }
  }

  const handleRemove = async () => {
    const fileId = avatar?.fileId
    if (fileId == null) {
      return
    }
    try {
      await deleteAvatar({
        accountId,
        userId,
        userData: { settings: { ...settings, assets: { avatar: null } } },
        keepEmptyItems: true,
      }).unwrap()
      await deleteFile({ accountId, fileId })
        .unwrap()
        .catch(() => undefined)
      patchAvatar(null)
    } catch {
      setError({ title: 'Could not remove photo', description: 'Please try again.' })
    }
  }

  if (userQuery.isLoading || isLazyDownload) {
    return 'Loading...'
  }

  return (
    <div className='d-flex flex-column gap-2'>
      <input ref={fileInputRef} type='file' accept={ACCEPTED_TYPES.join(',')} onChange={handleFilePicked} hidden />

      {uploading ? (
        <div className='d-inline-flex align-items-start gap-4 p-4 rounded zocks-border-gray-500'>
          <div className='d-flex flex-column align-items-center gap-2 pt-2'>
            <div className='d-flex align-items-center justify-content-center rounded-circle zocks-bg-color-gray-200' style={{ width: 84, height: 84 }}>
              <span className='fw-bold zocks-text-gray-900' style={{ fontSize: 28 }}>
                {initials}
              </span>
            </div>
            <span className='paragraph-extra-small zocks-text-gray-600'>no photo yet</span>
          </div>
          <div className='d-flex flex-1 flex-column align-items-center gap-2 py-4 rounded zocks-border-gray-600 zocks-border-dashed'>
            <div className='d-flex flex-column align-items-center'>
              <i className='bi bi-arrow-repeat' />
              <span className='paragraph-small zocks-text-gray-900'>Uploading {cropFileName}...</span>
            </div>
            <span className='paragraph-small zocks-text-primary'>{progress}%</span>
            <ProgressBar now={progress} style={{ width: 244, height: 4 }} />
          </div>
        </div>
      ) : isDeleting ? (
        <div className='paragraph-medium'>Deleting file...</div>
      ) : imageUrl ? (
        <div className='d-flex align-items-center gap-4 py-2'>
          <div className='position-relative'>
            <UserAvatar {...userAvatarProps} imageUrl={imageUrl} />
            <button
              type='button'
              onClick={openCurrentInCrop}
              className='btn p-0 position-absolute bottom-0 end-0 d-flex align-items-center justify-content-center rounded-circle bg-white border-0'
              style={{ width: 24, height: 24 }}
            >
              <i className='bi bi-pencil zocks-text-primary' />
            </button>
          </div>
          <div className='d-flex flex-column gap-2' style={{ width: 144 }}>
            <Button variant='primary' className='w-100' onClick={triggerBrowse}>
              Change photo
            </Button>
            <button
              type='button'
              onClick={handleRemove}
              className='btn btn-link p-0 d-flex align-items-center gap-1 paragraph-medium zocks-text-gray-900 text-decoration-underline'
            >
              <i className='bi bi-trash3' />
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div className='d-inline-flex align-items-start gap-4 p-4 rounded zocks-border-gray-500'>
          <div className='d-flex flex-column align-items-center gap-2 pt-2'>
            <div className='d-flex align-items-center justify-content-center rounded-circle zocks-bg-color-gray-200' style={{ width: 84, height: 84 }}>
              <span className='fw-bold zocks-text-gray-900' style={{ fontSize: 28 }}>
                {initials}
              </span>
            </div>
            <span className='paragraph-extra-small zocks-text-gray-600'>no photo yet</span>
          </div>
          <div
            className='d-flex flex-1 flex-column align-items-center gap-1 py-4 rounded zocks-bg-color-gray-100 zocks-border-gray-600 zocks-border-dashed'
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <i className='bi bi-upload' />
            <span className='paragraph-small fw-bold zocks-text-gray-900'>Drop a new photo here</span>
            <span className='paragraph-extra-small zocks-text-gray-700'>max 5 MB</span>
            <button type='button' onClick={triggerBrowse} className='btn btn-link p-0 paragraph-extra-small zocks-text-primary text-decoration-underline'>
              Browse files
            </button>
          </div>
        </div>
      )}

      {error ? (
        <div className='d-flex align-items-start gap-1'>
          <i className='bi bi-exclamation-circle zocks-text-danger-200' />
          <div>
            <div className='paragraph-small fw-bold zocks-text-danger-200'>{error.title}</div>
            <div className='paragraph-small zocks-text-danger-200'>{error.description}</div>
          </div>
        </div>
      ) : null}

      {cropSrc && <AvatarCropModal imageSrc={cropSrc} fileName={cropFileName} onCancel={closeCropModal} onConfirm={handleCropConfirm} />}
    </div>
  )
}
